"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";

const APP_IMG =
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/app.webp";
const QR_IMG =
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/qr_code.webp";
const APP_STORE_ICON =
  "https://ik.imagekit.io/mileseducation/miles_website/home/app-store.webp?tr=w-56,h-56,q-80";
const PLAY_STORE_ICON =
  "https://ik.imagekit.io/mileseducation/miles_website/home/play-store.webp?tr=w-56,h-56,q-80";

const APP_STORE_URL =
  "https://apps.apple.com/us/app/miles-us-pathway/id6504799221";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.miles.one";

const bullets = [
  "Get your CPE certificate instantly",
  "Track progress with an in-app CPE tracker",
  "Learn on any device, pick up right where you left off",
];

const StoreBadge = ({
  href,
  icon,
  label,
}: {
  href: string;
  icon: string;
  label: string;
}) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-2 rounded-[8px] bg-[rgba(255,255,255,0.10)] px-3 py-2 w-[100px] md:w-[160px] hover:bg-[rgba(255,255,255,0.16)] transition-colors"
  >
    <Image
      src={icon}
      alt={label}
      width={28}
      height={28}
      className="w-5 h-5 md:w-7 md:h-7 object-contain shrink-0"
      loading="lazy"
    />
    <div className="leading-tight">
      <p className="text-white text-[8px] md:text-[10px]">Download on</p>
      <p className="text-white text-[10px] md:text-sm font-semibold">{label}</p>
    </div>
  </Link>
);

const CairaLearning: React.FC = () => {
  return (
    <section className="w-full bg-black py-7 md:py-[50px]">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Title */}
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-[22px] md:text-[30px] font-bold text-[#FFBA08] leading-tight">
            Learning On The Go
          </h2>
          <p className="text-white text-sm md:text-base font-semibold mt-1">
            Masterclasses | Reels | Podcast | Live Webinars
          </p>
        </div>

        {/* Content: left copy + right phone */}
        <div className="flex flex-row items-start md:items-center gap-6 md:gap-10">
          {/* LEFT */}
          <div className="flex-1 min-w-0">
            <ul className="list-disc pl-5 space-y-1 md:space-y-3 text-white text-xs md:text-base">
              {bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>

            <p className="text-white mt-3 md:mt-8 mb-3 text-xs md:text-base">
              Download App
            </p>

            {/* QR — desktop only */}
            <Image
              src={QR_IMG}
              alt="Scan to download the Miles app"
              width={285}
              height={285}
              className="hidden md:block w-[130px] h-[130px] object-contain mb-4"
              loading="lazy"
            />

            {/* Store badges */}
            <div className="flex flex-col md:flex-row gap-3">
              <StoreBadge
                href={APP_STORE_URL}
                icon={APP_STORE_ICON}
                label="App Store"
              />
              <StoreBadge
                href={PLAY_STORE_URL}
                icon={PLAY_STORE_ICON}
                label="Play Store"
              />
            </div>
          </div>

          {/* RIGHT — phone */}
          <div className="flex-shrink-0">
            <Image
              src={APP_IMG}
              alt="Miles app — Power BI for Finance masterclass"
              width={652}
              height={1116}
              className="w-[150px] md:w-[320px] h-auto object-contain"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CairaLearning;
